import { DocumentType, calculators, entities, selectors } from "@fraction/shared";
import { useMemo, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import fraction from "src/api/fraction";
import { useBankAccountsQuery, useDocumentsQuery } from "src/apps/PostFundedDashboard/queries";
import { useAuth } from "src/auth";
import { useApplicationQuery } from "src/hooks/useApplication";
import { useMutation, useQuery } from "src/lib";

export function usePlaid({
  applicationId,
  purpose,
  onSuccess,
}: {
  applicationId?: string;
  purpose: "underwriting" | "payments";
  onSuccess?: (accounts: entities.BankAccount[] | undefined) => void;
}) {
  const { refetch: refetchBankAccounts } = useBankAccountsQuery();

  const { updateLocalUser, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [plaidToken, setPlaidToken] = useState<string | null>(null);
  const [shouldPollBankStatements, setShouldPollBankStatements] = useState(false);
  const {
    refetch,
    data: app,
    isLoading: isLoadingApp,
  } = useApplicationQuery({
    id: applicationId,
    initialRefetch: true,
    accountType: "applicant",
  });

  const {
    refetch: refetchBankStatements,
    data: bankStatements,
    isLoading: isLoadingBankStatements,
  } = useDocumentsQuery(applicationId, {
    initialRefetch: true,
    select: (data) =>
      data.filter((doc) => doc.type?.name === DocumentType.BANKSTMTS && doc?.source === "plaid"),
  });

  // Add polling query to check for bank statements
  useQuery({
    queryKey: ["pollBankStatements", applicationId],
    enabled: !!applicationId && shouldPollBankStatements,
    queryFn: async () => {
      console.log("polling banks tatements");
      const statements = await refetchBankStatements();
      if (statements?.data?.length) {
        setShouldPollBankStatements(false);
        return statements.data;
      }
      return [];
    },
    retry: true,
    retryDelay: 1000, // Retry every second
    refetchInterval: 1000, // Poll every second
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });

  const debts = useMemo(
    () => (app ? calculators.application.selectAllDebts(app)?.filter((debt) => debt?.plaidItemId) : []),
    [app]
  );
  const assets = useMemo(
    () => (app ? calculators.application.selectAllAssets(app)?.filter((asset) => asset?.plaidItemId) : []),
    [app]
  );
  const applicantIncomes = app ? selectors.application.selectAllIncomesInApplication(app) : [];
  const incomes = useMemo(() => {
    return applicantIncomes?.filter((income) => income?.externalId && income?.externalSource === "plaid");
  }, [applicantIncomes]);

  const registerMutation = useMutation({
    mutationFn: async (token: string) => {
      if (!applicationId) {
        throw new Error("No active application");
      }
      return fraction.registerPlaidBank(token, applicationId, purpose);
    },
    onSuccess: async ({ plaidToken }) => {
      updateLocalUser({ plaidToken });
      setShouldPollBankStatements(true);
      const [bankAccounts] = await Promise.all([refetchBankAccounts(), refetch()]);
      onSuccess?.(bankAccounts?.data);
    },
  });

  const linkTokenMutation = useMutation({
    mutationFn: async () => {
      const data = await fraction.getPlaidLinkToken(purpose);
      setPlaidToken(data);
      return data;
    },
    onSuccess: () => {
      setLoading(true);
    },
  });

  const link = usePlaidLink({
    token: linkTokenMutation.data || null,
    onLoad: () => {
      setLoading(false);
    },
    onSuccess: async (public_token, metadata) => {
      setLoading(false);
      // send public_token to server to exchange for access_token
      await registerMutation.mutateAsync(public_token);
    },
  });

  const start = useMutation({
    mutationFn: async () => {
      if (plaidToken) {
        await link.open();
      } else {
        await linkTokenMutation.mutateAsync();
      }
    },
  });

  useQuery({
    queryKey: ["linkOpen", linkTokenMutation.data, link.ready],
    enabled: !!linkTokenMutation.data && link.ready,
    queryFn: async () => {
      await link.open();
      return null;
    },
  });

  return {
    ...link,
    ready: !!token,
    isLoading: loading || linkTokenMutation.isPending || registerMutation.isPending || start.isPending,
    start: start.mutateAsync,
    registerMutation,
    bankStatements,
    isLoadingBankStatements: isLoadingBankStatements || shouldPollBankStatements,
    debts,
    assets,
    incomes,
    isLoadingApp,
  };
}
